import React from "react";

// Shared skeleton (shimmer) placeholders for the storefront.
//
// Why skeletons instead of the spinners/backdrops these replaced: a centred
// spinner tells you "something is happening" but not what, and a modal backdrop
// actively blocks the page. A skeleton keeps the page's real shape on screen,
// so content doesn't jump when it arrives.
//
// Motion is disabled under prefers-reduced-motion, matching RouteFallback.

// One shimmer block. Sized entirely by the caller via className.
export const Skeleton = ({ className = "" }) => (
  <div
    aria-hidden="true"
    className={`animate-pulse rounded bg-[#ece0cd] motion-reduce:animate-none ${className}`}
  />
);

// Announces "loading" ONCE for a group. The blocks themselves are aria-hidden,
// so a screen reader hears a single status rather than a wall of empty divs.
export const SkeletonRegion = ({ label = "Loading", className = "", children }) => (
  <div role="status" aria-busy="true" aria-label={label} className={className}>
    {children}
  </div>
);

// Mirrors HomeProductCard: 3:4 image, brand, title, price row.
export const ProductCardSkeleton = () => (
  <div className="mx-1 overflow-hidden rounded-2xl border border-[#eadcc0] bg-white shadow-sm">
    <Skeleton className="aspect-[3/4] w-full rounded-none" />
    <div className="p-4">
      <Skeleton className="h-3 w-1/3" />
      <Skeleton className="mt-2 h-4 w-3/4" />
      <div className="mt-3 flex items-center gap-2">
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-4 w-12" />
      </div>
    </div>
  </div>
);

// Same grid classes the real listing uses, so the layout doesn't shift.
export const ProductGridSkeleton = ({
  count = 8,
  className = "grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 xl:grid-cols-4",
  label = "Loading products",
}) => (
  <SkeletonRegion label={label} className={className}>
    {Array.from({ length: count }, (_, i) => (
      <ProductCardSkeleton key={i} />
    ))}
  </SkeletonRegion>
);

// Horizontal rail (homepage sections). Cards sized to roughly match the
// carousel's responsive item widths.
export const ProductRailSkeleton = ({ count = 5 }) => (
  <SkeletonRegion label="Loading products" className="flex gap-3 overflow-hidden">
    {Array.from({ length: count }, (_, i) => (
      <div
        key={i}
        className="w-1/2 shrink-0 sm:w-1/3 lg:w-1/5"
      >
        <ProductCardSkeleton />
      </div>
    ))}
  </SkeletonRegion>
);

// Product detail: gallery on the left, buy box on the right.
export const ProductDetailsSkeleton = () => (
  <SkeletonRegion
    label="Loading product"
    className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-10 lg:grid-cols-2"
  >
    <div>
      <Skeleton className="aspect-[3/4] w-full rounded-2xl" />
      <div className="mt-4 flex gap-3">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-20 w-16 rounded-lg" />
        ))}
      </div>
    </div>
    <div>
      <Skeleton className="h-3 w-24" />
      <Skeleton className="mt-3 h-8 w-3/4" />
      <Skeleton className="mt-3 h-5 w-1/3" />
      <div className="mt-6 flex items-center gap-3">
        <Skeleton className="h-8 w-28" />
        <Skeleton className="h-6 w-20" />
      </div>
      <Skeleton className="mt-8 h-4 w-20" />
      <div className="mt-3 flex gap-2">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-10 w-14 rounded-lg" />
        ))}
      </div>
      <Skeleton className="mt-8 h-12 w-full rounded-full" />
      <Skeleton className="mt-3 h-12 w-full rounded-full" />
      <div className="mt-8 space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-11/12" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    </div>
  </SkeletonRegion>
);

// A cart / wishlist style line item: thumbnail + two text lines.
export const LineItemSkeleton = () => (
  <div className="flex gap-4 rounded-2xl border border-[#eadcc0] bg-white p-4 shadow-sm">
    <Skeleton className="h-28 w-24 shrink-0 rounded-xl" />
    <div className="flex-1">
      <Skeleton className="h-4 w-2/5" />
      <Skeleton className="mt-2 h-3 w-1/4" />
      <Skeleton className="mt-4 h-5 w-24" />
      <Skeleton className="mt-3 h-3 w-1/3" />
    </div>
  </div>
);

export const LineItemListSkeleton = ({ count = 3, label = "Loading" }) => (
  <SkeletonRegion label={label} className="space-y-4">
    {Array.from({ length: count }, (_, i) => (
      <LineItemSkeleton key={i} />
    ))}
  </SkeletonRegion>
);

// Order history rows: an order card with a status line and one product row.
export const OrderListSkeleton = ({ count = 3 }) => (
  <SkeletonRegion label="Loading orders" className="space-y-4">
    {Array.from({ length: count }, (_, i) => (
      <div
        key={i}
        className="rounded-2xl border border-[#eadcc0] bg-white p-5 shadow-sm"
      >
        <div className="flex items-center justify-between">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>
        <div className="mt-4 flex gap-4">
          <Skeleton className="h-24 w-20 shrink-0 rounded-xl" />
          <div className="flex-1">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="mt-2 h-3 w-1/4" />
            <Skeleton className="mt-4 h-4 w-20" />
          </div>
        </div>
      </div>
    ))}
  </SkeletonRegion>
);

// Single order detail: tracker, item, address and price panels.
export const OrderDetailsSkeleton = () => (
  <SkeletonRegion
    label="Loading order"
    className="mx-auto max-w-6xl space-y-6 px-4 py-8"
  >
    <Skeleton className="h-8 w-56" />
    <div className="rounded-2xl border border-[#eadcc0] bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="flex flex-1 flex-col items-center gap-2">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>
    </div>
    <LineItemSkeleton />
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {Array.from({ length: 2 }, (_, i) => (
        <div
          key={i}
          className="rounded-2xl border border-[#eadcc0] bg-white p-6 shadow-sm"
        >
          <Skeleton className="h-3 w-28" />
          <div className="mt-4 space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-5/6" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  </SkeletonRegion>
);

export default Skeleton;
